import type { components } from "@majitap/sdk";
import {
  calendarDay,
  currentView,
  dayRange,
  type FilterField,
  type FilterOption,
  type ListViewDef,
  readViews,
  type Search,
  viewsKey,
  writeViews,
} from "@majitap/ui";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { CalendarClock, Factory, IdCard, Map as MapIcon, Truck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api, ok } from "./api";
import { meQuery } from "./session";
import { zonesListQuery } from "./zones";

export type FilterOptions = components["schemas"]["DeliveryFilterOptions"];

/** Drivers, trucks and fill sites by name, to filter orders and deliveries by (P8-57). */
export const filterOptionsQuery = queryOptions({
  queryKey: ["dispatch", "filter-options"],
  staleTime: 5 * 60_000,
  queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/filter-options", {})),
});

const ICON = { size: 15, "aria-hidden": true } as const;

/** A label map as filter options: `{ pending: ["Pending", tone] }` → Pending. */
export const optionsOf = <K extends string>(labels: Record<K, string | [string, unknown]>): FilterOption[] =>
  (Object.entries(labels) as [K, string | [string, unknown]][]).map(([value, l]) => ({
    value,
    label: Array.isArray(l) ? (l[0] as string) : l,
  }));

/** The filters every list of deliveries shares: zone, driver, truck and fill site, by name. */
export function useFleetFields(group: string): FilterField[] {
  const zones = useQuery(zonesListQuery);
  const options = useQuery(filterOptionsQuery);
  const o = options.data;
  return [
    {
      id: "zone",
      label: "Zone",
      group: "Place",
      icon: <MapIcon {...ICON} />,
      kind: "options",
      param: "zone",
      nullable: true,
      emptyLabel: "No zone",
      options: (zones.data ?? []).map((z) => ({ value: z.id, label: z.name })),
    },
    {
      id: "driver",
      label: "Driver",
      group,
      icon: <IdCard {...ICON} />,
      kind: "options",
      param: "driver",
      nullable: true,
      emptyLabel: "No driver yet",
      options: (o?.drivers ?? []).map((d) => ({ value: d.id, label: d.name, hint: d.owner ?? "Own fleet" })),
    },
    {
      id: "truck",
      label: "Truck",
      group,
      icon: <Truck {...ICON} />,
      kind: "options",
      param: "truck",
      nullable: true,
      emptyLabel: "No truck yet",
      options: (o?.trucks ?? []).map((t) => ({ value: t.id, label: t.plate_number, hint: t.owner ?? "Own fleet" })),
    },
    {
      id: "fillsite",
      label: "Fill site",
      group,
      icon: <Factory {...ICON} />,
      kind: "options",
      param: "fillsite",
      nullable: true,
      emptyLabel: "No fill site yet",
      options: (o?.fillsites ?? []).map((f) => ({ value: f.id, label: f.name })),
    },
  ];
}

export const datesField = (id: string, label: string, group: string, from = "from", to = "to"): FilterField => ({
  id,
  label,
  group,
  icon: <CalendarClock {...ICON} />,
  kind: "dates",
  from,
  to,
});

/** Whole days around today in the company's zone, as the API's from/to: `days` long, starting `back` days ago. */
export function daysRange(timezone: string, days = 1, back = 0): { from: string; to: string } {
  const today = calendarDay(new Date().toISOString(), timezone);
  const day = (offset: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
  return dayRange({ start: day(-back), end: day(days - 1 - back) }, timezone);
}

/**
 * A person's saved views of one list (P8-57): kept in this browser, per company and person.
 * Sharing them between staff would need the API; not yet.
 */
export function useSavedViews(list: string) {
  const { data: me } = useSuspenseQuery(meQuery);
  const key = viewsKey(list, me?.active_company?.id ?? "none", me?.user.id ?? "none");
  const [views, setViews] = useState<ListViewDef[]>([]);
  useEffect(() => setViews(readViews(key).map((v) => ({ ...v, saved: true }))), [key]);
  const store = useCallback(
    (next: ListViewDef[]) => {
      setViews(next);
      writeViews(key, next);
    },
    [key],
  );
  const save = (label: string, search: Search) =>
    store([...views, { id: `saved-${Date.now().toString(36)}`, label, search, saved: true }]);
  const remove = (id: string) => store(views.filter((v) => v.id !== id));
  return { views, save, remove };
}

/** The view on screen: the one whose filters the URL holds, if any. */
export const viewOf = (views: ListViewDef[], search: Search, keys: string[]) => currentView(views, search, keys)?.id;
